/**
 * SanctionBadge — E-90, Story 1.3
 * Displays payment ban and booking restriction badges on partner cards.
 */

import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';

interface SanctionBadgeProps {
  transferBanLevel: string;
  transferBanUntil?: string | null | undefined;
  bookingRestricted?: boolean | undefined;
  bookingRestrictedUntil?: string | null | undefined;
  bookingWarningFlag?: boolean | undefined;
  compact?: boolean | undefined;
}

export function SanctionBadge({
  transferBanLevel,
  transferBanUntil,
  bookingRestricted,
  bookingRestrictedUntil,
  bookingWarningFlag,
  compact,
}: SanctionBadgeProps) {
  const { t } = useTranslation();

  const badges: React.ReactNode[] = [];

  // Payment transfer ban badge
  if (transferBanLevel && transferBanLevel !== 'NONE') {
    const labelKey =
      transferBanLevel === 'PERMANENT'
        ? 'partner.sanctions.transferBanPermanent'
        : transferBanLevel === 'SIX_MONTH'
          ? 'partner.sanctions.transferBanSixMonth'
          : 'partner.sanctions.transferBanThreeMonth';

    badges.push(
      <Badge key="transfer-ban" variant="destructive" className="text-xs">
        {compact ? '🚫' : t(labelKey)}
        {!compact && transferBanUntil && (
          <span className="ml-1 text-muted-foreground">
            (
            {t('partner.sanctions.banUntil', {
              date: new Date(transferBanUntil).toLocaleDateString('hu-HU'),
            })}
            )
          </span>
        )}
      </Badge>
    );
  }

  // Booking restriction badge
  if (bookingRestricted) {
    badges.push(
      <Badge
        key="booking-ban"
        variant="outline"
        className="border-orange-500 text-orange-600 text-xs"
      >
        {compact ? '🚶' : t('partner.sanctions.walkInOnly')}
        {!compact && bookingRestrictedUntil && (
          <span className="ml-1 text-muted-foreground">
            ({new Date(bookingRestrictedUntil).toLocaleDateString('hu-HU')})
          </span>
        )}
      </Badge>
    );
  }

  // Booking warning flag (red exclamation)
  if (bookingWarningFlag && !bookingRestricted) {
    badges.push(
      <span
        key="booking-warning"
        className="text-destructive text-lg"
        title={t('partner.sanctions.bookingWarning')}
      >
        ❗
      </span>
    );
  }

  if (badges.length === 0) return null;

  return <div className="flex items-center gap-1 flex-wrap">{badges}</div>;
}
